import { useState } from "react";
import { Alert, Checkbox, Input, Modal, Typography, message } from "antd";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { ApiService, FormTypeEnum, PhaseEnum } from "../../api";
import { apiErrorMessage } from "../../api/apiError";

const { Text } = Typography;

type Props = {
  open: boolean;
  templateId: number;
  /** The step the new section drops into, if it was started from one. */
  stageKey: string | null;
  onClose: () => void;
  /** Hands back the created section so the draft can place it immediately. */
  onCreated: (section: { id: number; title: string }, stageKey: string | null) => void;
};

/**
 * An empty Formily document: a vertical form layout with nothing in it.
 *
 * A section has to have a schema to exist -- `schema_json` is not nullable --
 * so creating one from here means creating an empty form and sending the person
 * to the form designer to fill it. That is the honest order of operations, and
 * it is much better than the previous one, which was no order of operations at
 * all: sections could only be made on a different screen, so a data-entry step
 * created here had nothing to put in it and no way to say so.
 */
const emptySchema = () => ({
  form: { labelCol: 6, wrapperCol: 12 },
  schema: {
    type: "object",
    properties: {
      layout: {
        type: "void",
        "x-component": "FormLayout",
        "x-component-props": { layout: "vertical", labelAlign: "left" },
        properties: {},
      },
    },
  },
});

const CreateSectionModal = ({
  open,
  templateId,
  stageKey,
  onClose,
  onCreated,
}: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [title, setTitle] = useState("");
  const [openDesigner, setOpenDesigner] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const close = () => {
    setTitle("");
    setError("");
    onClose();
  };

  const submit = async () => {
    if (!title.trim()) {
      setError(t("Give the section a name."));
      return;
    }
    setSaving(true);
    setError("");
    try {
      const created: any = await ApiService.apiMainFormsCreate({
        template: templateId,
        title: title.trim(),
        type: FormTypeEnum.DYNAMIC,
        order: 1,
        phase: PhaseEnum.DRAFT,
        schema_json: emptySchema(),
        // Published straight away: an unpublished section has no schema for a
        // case to render, and an empty published one is harmless.
        publish: true,
        note: "Created from the process designer",
      });
      onCreated({ id: created.id, title: created.title }, stageKey);
      message.success(t("Section created"));
      close();
      if (openDesigner) navigate(`/forms/${created.id}/design`);
    } catch (e: any) {
      // Never swallow a refusal: a Save button that does nothing is the worst
      // symptom this codebase has produced.
      setError(apiErrorMessage(e, t("Something went wrong")));
    }
    setSaving(false);
  };

  return (
    <Modal
      open={open}
      onCancel={close}
      onOk={submit}
      confirmLoading={saving}
      okText={t("Create it")}
      cancelText={t("Cancel")}
      title={t("New section")}
    >
      <Text type="secondary">
        {t(
          "A section is one block of questions inside a step. It starts empty — the form designer is where the questions go."
        )}
      </Text>

      <Input
        style={{ marginTop: 12 }}
        autoFocus
        value={title}
        placeholder={t("e.g. Hazards and controls")}
        onChange={(e) => setTitle(e.target.value)}
        onPressEnter={submit}
      />

      <Checkbox
        style={{ marginTop: 12 }}
        checked={openDesigner}
        onChange={(e) => setOpenDesigner(e.target.checked)}
      >
        {t("Open the form designer now")}
      </Checkbox>

      {stageKey && (
        <div style={{ marginTop: 8 }}>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {t(
              "It will be placed in the step you started from. Remember to save the process."
            )}
          </Text>
        </div>
      )}

      {error && <Alert type="error" showIcon style={{ marginTop: 12 }} message={error} />}
    </Modal>
  );
};

export default CreateSectionModal;
